/**
 * Reopening a message you recorded, while it is still undelivered.
 *
 * Deliberately the same decrypt path as decryptOwnerLetterReply — the CEK is
 * HPKE-wrapped to the owner's device key and the AES-GCM AAD is the message
 * id. Anything that diverges here would be a second crypto path to get wrong.
 *
 * The boundary the server enforces and this copy reflects: once a message has
 * been DELIVERED it cannot be reopened. It belongs to the person it was sent
 * to at that point, and taking it back is not ours to offer.
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - vendored JS has no types
import { unwrapCekForRecipient } from '../../../vendor/crypto/cek.js';

import type { ApiClient } from '../apiClient';
import { aesGcmDecrypt, base64ToBytes, loadOwnerPrivateKey } from '../ownerDeviceKey';

export interface OwnerReviewContext {
  message_id: string;
  format: string;
  cek_ciphertext: string;
  iv_b64: string;
  letter_ciphertext_b64: string | null;
  download_url: string | null;
  mime_type: string | null;
}

export function getReviewContext(
  api: ApiClient,
  vaultId: string,
  messageId: string,
): Promise<OwnerReviewContext> {
  return api.get(`/api/vaults/${vaultId}/messages/${messageId}/review-context`);
}

/** Reopen a letter you wrote. Returns the text, decrypted in this browser. */
export async function reviewOwnLetter(args: {
  api: ApiClient;
  vaultId: string;
  messageId: string;
  ownerEmail: string;
  privateKeyOverride?: CryptoKey;
}): Promise<string> {
  const ctx = await getReviewContext(args.api, args.vaultId, args.messageId);
  if (ctx.letter_ciphertext_b64 === null) {
    throw new Error('This message has no readable text.');
  }
  const privKey = args.privateKeyOverride ?? (await loadOwnerPrivateKey(args.ownerEmail));
  const cek: Uint8Array = await unwrapCekForRecipient(ctx.cek_ciphertext, privKey, ctx.message_id);
  try {
    const plaintext = await aesGcmDecrypt({
      key: cek,
      iv: base64ToBytes(ctx.iv_b64),
      ciphertext: base64ToBytes(ctx.letter_ciphertext_b64),
      aad: new TextEncoder().encode(ctx.message_id),
    });
    return new TextDecoder().decode(plaintext);
  } finally {
    cek.fill(0);
  }
}

/**
 * Reopen a video or voice message. Returns a blob URL the caller must revoke.
 * The media is downloaded, then decrypted here — it is never decrypted server
 * side, so a large video costs memory in the tab; acceptable for review of
 * something the member recorded themselves on this class of device.
 */
export async function reviewOwnMedia(args: {
  api: ApiClient;
  vaultId: string;
  messageId: string;
  ownerEmail: string;
}): Promise<{ url: string; mimeType: string }> {
  const ctx = await getReviewContext(args.api, args.vaultId, args.messageId);
  if (ctx.download_url === null) {
    throw new Error('This message has no recording to play.');
  }
  const privKey = await loadOwnerPrivateKey(args.ownerEmail);
  const cek: Uint8Array = await unwrapCekForRecipient(ctx.cek_ciphertext, privKey, ctx.message_id);
  try {
    const res = await fetch(ctx.download_url);
    if (!res.ok) throw new Error('Could not fetch your recording.');
    const ciphertext = new Uint8Array(await res.arrayBuffer());
    const plaintext = await aesGcmDecrypt({
      key: cek,
      iv: base64ToBytes(ctx.iv_b64),
      ciphertext,
      aad: new TextEncoder().encode(ctx.message_id),
    });
    const mimeType = ctx.mime_type ?? 'video/webm';
    const blob = new Blob([plaintext as BlobPart], { type: mimeType });
    return { url: URL.createObjectURL(blob), mimeType };
  } finally {
    cek.fill(0);
  }
}
